import { HeaderPage, LinkButton, Paginator, PopUpGeneric, TableGeneric } from '@components/index'
import { defaultPagination } from '@components/paginator/paginator-utils'
import { IHighlights } from '@interfaces/highlights/IHighlights'
import { IColumn, IListPaginated } from '@interfaces/index'
import { errorHandler } from '@services/index'
import HighlightService from '@services/requests/highlightService'
import { formatDate } from '@utils/dates/dateUtils'
import { totalPages } from '@utils/index'
import React from 'react'
import { confirmAlert } from 'react-confirm-alert'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

export default function Highlights(): React.ReactElement {
    const navigate = useNavigate()
    const [data, setData] = React.useState<IListPaginated<IHighlights>>(defaultPagination)
    const [isLoading, setIsLoading] = React.useState(false)

    const queryPage = useQuery().get('page')

    function useQuery(): URLSearchParams {
        return new URLSearchParams(useLocation().search)
    }

    function init(): void {
        if (queryPage)
            getList(parseInt(queryPage))
        else
            getList(1)
    }

    React.useEffect(() => {
        init()
    }, [queryPage])

    async function getList(page: number, perPage?: number, sortBy?: string): Promise<void> {
        setIsLoading(true)
        try {
            const response = await HighlightService.list(page, perPage, sortBy)
            if (page !== 1 && response.items.length <= 0) {
                navigate(`?page=${page - 1}`)
            } else {
                setData(response)
            }
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    async function deleteItem(id: number): Promise<void> {
        try {
            const response = await HighlightService.delete(id)
            toast(response.message, { type: 'success' })
            init()
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        }
    }

    function popUpConfirm(id: number): void {
        confirmAlert({
            customUI: ({ onClose }) => (
                <PopUpGeneric
                    onConfirm={() => {
                        deleteItem(id)
                        onClose()
                    }}
                    onCancel={onClose}
                    title={'Deseja excluir este destaque?'}
                />
            ),
        })
    }

    const columns: IColumn<IHighlights>[] = React.useMemo<IColumn<IHighlights>[]>(
        () => [
            {
                header: 'Data de publicação',
                dataIndex: 'publicationDate',
                render: (item) => formatDate(item.publicationDate),
            },
            {
                header: 'Descrição',
                dataIndex: 'description',
            },
            {
                header: 'Ação',
                dataIndex: 'actions',
                align: 'align-center',
                size: '8rem',
                render: (item) => (
                    <div className="col-actions">
                        <LinkButton title='Editar' path={`/destaques/${item.id}/editar`} />
                        <button role="button" className='link-button' onClick={() => popUpConfirm(item.id)}>
                            Excluir
                        </button>
                    </div>
                ),
            },
        ],
        [data]
    )
    return (
        <div className="card">
            <div className="highlights">
                <HeaderPage title="Destaques" textButton="Novo Destaque" onClickButton={() => navigate('/destaques/novo')} />
                <div>
                    <TableGeneric data={data.items} columns={columns} isLoading={isLoading} />
                    <div className="paginator">
                        <Paginator maxElements={7} current={data.page} total={totalPages(data.count, data.perPage)} onClick={(page) => navigate(`/destaques?page=${page}`)} />
                    </div>
                </div>
            </div>
        </div>
    )
}
